import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import {
  addClientToMatter,
  updateClientRole,
  removeClientFromMatter,
} from '../../store/actions/Matters/MatterActions';
import {
  PiSpinnerLight,
  PiTrashLight,
  PiPlusLight,
  PiUserLight,
  PiBuildingLight,
  PiEnvelopeLight,
  PiPhoneLight,
} from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import apis from '../../utils/apis';
import Spinner from '../shared/TailwindComponents/Spinner';

interface ExistingMatterClient {
  client_id: number;
  client_uuid: string;
  name: string;
  email?: string;
  role_id: string | number;
  role_name: string;
}

interface RawClient {
  id: number;
  uuid: string;
  name: string;
  contact_email?: string | null;
  contact_person_email?: string | null;
  contact_person_name?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  is_individual?: boolean;
  matters_count?: number;
}

interface AddClientToMatterModalProps {
  matterUuid: string;
  clientRoleOptions?: DropdownOption[];
  existingMatterClients?: ExistingMatterClient[];
  onClientAdded?: () => void | Promise<void>;
}

const AddClientToMatterModal: React.FC<AddClientToMatterModalProps> = ({
  matterUuid,
  clientRoleOptions = [],
  existingMatterClients = [],
  onClientAdded,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [allClients, setAllClients] = useState<RawClient[]>([]);
  const [isFetchingClients, setIsFetchingClients] = useState(false);
  const [matterClients, setMatterClients] = useState<ExistingMatterClient[]>(existingMatterClients);

  const [selectedClientUuid, setSelectedClientUuid] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>(
    String(clientRoleOptions[0]?.value || '')
  );
  const [isAdding, setIsAdding] = useState(false);
  const [pendingChange, setPendingChange] = useState<string | null>(null);

  useEffect(() => {
    setMatterClients(existingMatterClients);
  }, [existingMatterClients]);

  // Fetch the firm-wide contacts list. Backend returns:
  //   { data: [...], page, page_size, success, total, total_pages }
  useEffect(() => {
    const fetchClients = async () => {
      setIsFetchingClients(true);
      try {
        const response = await apis.get('/clients');
        const payload = response?.data;
        const list: RawClient[] = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.clients)
            ? payload.clients
            : Array.isArray(payload)
              ? payload
              : [];
        setAllClients(list);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setAllClients([]);
      } finally {
        setIsFetchingClients(false);
      }
    };
    fetchClients();
  }, []);

  const existingUuidSet = useMemo(
    () => new Set(matterClients.map((c) => c.client_uuid)),
    [matterClients]
  );

  // Lookup map for enriching linked-contact rows with directory metadata
  // (is_individual, phone, address) that the matter endpoint doesn't return.
  const allClientsByUuid = useMemo(() => {
    const map = new Map<string, RawClient>();
    for (const c of allClients) {
      if (c.uuid) map.set(c.uuid, c);
    }
    return map;
  }, [allClients]);

  const availableClients = useMemo(
    () =>
      allClients
        .filter((c) => !existingUuidSet.has(c.uuid))
        .map((c) => ({
          value: c.uuid,
          label: c.name || t('Unknown contact'),
        })),
    [allClients, existingUuidSet, t]
  );

  const refresh = useCallback(async () => {
    if (onClientAdded) await onClientAdded();
  }, [onClientAdded]);

  const handleAdd = async () => {
    if (!selectedClientUuid || !matterUuid) return;
    setIsAdding(true);
    try {
      await dispatch(addClientToMatter(matterUuid, selectedClientUuid, selectedRole) as any);

      const added = allClients.find((c) => c.uuid === selectedClientUuid);
      if (added) {
        const roleLabel =
          clientRoleOptions.find((r) => String(r.value) === String(selectedRole))?.label ||
          String(selectedRole);
        setMatterClients((prev) => [
          ...prev,
          {
            client_id: added.id,
            client_uuid: added.uuid,
            name: added.name,
            email: added.contact_email || added.contact_person_email || '',
            role_id: selectedRole,
            role_name: String(roleLabel),
          },
        ]);
      }
      setSelectedClientUuid('');
      await refresh();
    } catch (error) {
      console.error('Error adding contact:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleChangeRole = async (client: ExistingMatterClient, newRoleId: string) => {
    setPendingChange(`role-${client.client_uuid}`);
    try {
      await dispatch(updateClientRole(matterUuid, client.client_uuid, newRoleId) as any);
      const roleLabel =
        clientRoleOptions.find((r) => String(r.value) === String(newRoleId))?.label || newRoleId;
      // Match on client_uuid: the matter detail endpoint returns rows without
      // a numeric client_id, so matching on that key would update every row
      // (undefined === undefined is true).
      setMatterClients((prev) =>
        prev.map((c) =>
          c.client_uuid === client.client_uuid
            ? { ...c, role_id: newRoleId, role_name: String(roleLabel) }
            : c
        )
      );
      await refresh();
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setPendingChange(null);
    }
  };

  const handleRemove = async (client: ExistingMatterClient) => {
    setPendingChange(`remove-${client.client_uuid}`);
    try {
      await dispatch(removeClientFromMatter(matterUuid, client.client_uuid) as any);
      setMatterClients((prev) => prev.filter((c) => c.client_uuid !== client.client_uuid));
      await refresh();
    } catch (error) {
      console.error('Error removing contact:', error);
    } finally {
      setPendingChange(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar-main">
        {/* Existing matter contacts */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('Linked Contacts')}{' '}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({matterClients.length})
              </span>
            </h3>
          </div>

          {matterClients.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('No contacts are linked to this matter yet.')}
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {matterClients.map((client) => {
                const removing = pendingChange === `remove-${client.client_uuid}`;
                const changingRole = pendingChange === `role-${client.client_uuid}`;
                const directoryEntry = allClientsByUuid.get(client.client_uuid);
                // Default to organization icon - most matter contacts are companies.
                // Only flip to person when the directory explicitly says so.
                const isIndividual = directoryEntry?.is_individual === true;
                const Icon = isIndividual ? PiUserLight : PiBuildingLight;
                const phone = directoryEntry?.contact_phone || '';
                const email =
                  client.email ||
                  directoryEntry?.contact_email ||
                  directoryEntry?.contact_person_email ||
                  '';
                return (
                  <li
                    key={client.client_uuid}
                    className="glass rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30">
                          <Icon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                            title={client.name}
                          >
                            {client.name}
                          </p>
                          <div className="mt-1 space-y-0.5">
                            {email && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                                <PiEnvelopeLight className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{email}</span>
                              </div>
                            )}
                            {phone && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <PiPhoneLight className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="destructive"
                        size="small"
                        onClick={() => handleRemove(client)}
                        disabled={removing}
                        ariaLabel={t('Remove contact')}
                      >
                        {removing ? (
                          <Spinner size="glyph" className="!h-4 !w-4" />
                        ) : (
                          <PiTrashLight className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <FieldShell label={t('Role on this matter')} translate={false}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          {changingRole ? (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700">
                              <Spinner size="glyph" className="!h-4 !w-4" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t('Updating role...')}
                              </span>
                            </div>
                          ) : (
                            <Dropdown
                              options={clientRoleOptions}
                              value={String(client.role_id)}
                              placeholder={client.role_name}
                              onSelect={(option) => {
                                const v = Array.isArray(option.value)
                                  ? option.value[0]
                                  : option.value;
                                if (String(v) !== String(client.role_id)) {
                                  handleChangeRole(client, String(v));
                                }
                              }}
                              className="w-full"
                            />
                          )}
                        </div>
                      </div>
                      </FieldShell>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Add a new contact */}
        <section>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            {t('Link a Contact')}
          </h3>

          {isFetchingClients ? (
            <div className="flex items-center gap-2 p-4">
              <Spinner size="glyph" className="!h-4 !w-4" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('Loading contacts...')}
              </span>
            </div>
          ) : availableClients.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {allClients.length === 0
                  ? t('No contacts found in your organization yet.')
                  : t('All contacts are already linked to this matter.')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Dropdown
                  label={t('Contact')}
                  options={availableClients}
                  value={selectedClientUuid}
                  placeholder={t('Select a contact')}
                  onSelect={(option) => {
                    const v = Array.isArray(option.value) ? option.value[0] : option.value;
                    setSelectedClientUuid(String(v));
                  }}
                  className="w-full"
                />
              </div>
              <div>
                <Dropdown
                  label={t('Role')}
                  options={clientRoleOptions}
                  value={selectedRole}
                  placeholder={t('Select a role')}
                  onSelect={(option) => {
                    const v = Array.isArray(option.value) ? option.value[0] : option.value;
                    setSelectedRole(String(v));
                  }}
                  className="w-full"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleAdd}
                  disabled={!selectedClientUuid || !selectedRole || isAdding}
                  icon={
                    isAdding ? (
                      <Spinner size="glyph" className="!h-4 !w-4" />
                    ) : (
                      <PiPlusLight className="w-4 h-4" />
                    )
                  }
                >
                  {isAdding ? t('Linking...') : t('Link Contact')}
                </Button>
              </div>
            </div>
          )}
        </section>

      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end">
        <Button variant="neutral" size="small" onClick={() => dispatch(hideSidePanel())}>
          {t('Close')}
        </Button>
      </div>
    </div>
  );
};

export default AddClientToMatterModal;
